/**
 * User Admin CSV import/export columns and cell formatting.
 * Import columns match the sample file; export adds read-only status fields.
 */
import dayjs from "dayjs";
import { escapeCsvCell } from "./csvFileHelpers";
import { isPlaceholderEmail } from "./displayEmail";

/** Columns accepted by User Admin import (header aliases live in the page parser). */
export const USER_ADMIN_CSV_IMPORT_COLUMN_IDS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "role",
];

/** Export includes import columns plus list-page status fields. */
export const USER_ADMIN_CSV_COLUMN_IDS = [
  ...USER_ADMIN_CSV_IMPORT_COLUMN_IDS,
  "login_status",
  "last_login",
  "email_verified",
  "created",
];

export const USER_ADMIN_CSV_IMPORT_HEADER_LINE = USER_ADMIN_CSV_IMPORT_COLUMN_IDS.join(",");
export const USER_ADMIN_CSV_HEADER_LINE = USER_ADMIN_CSV_COLUMN_IDS.join(",");

/**
 * @param {Record<string, unknown>|null|undefined} user
 * @returns {string[]}
 */
export function getUserAdminCsvRoles(user) {
  if (Array.isArray(user?.roles) && user.roles.length) {
    return user.roles.map((r) => String(r).trim()).filter(Boolean);
  }
  if (user?.role != null && String(user.role).trim()) {
    return [String(user.role).trim()];
  }
  return [];
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatUserAdminTimestampForCsv(value) {
  if (value == null || value === "") return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("MM/DD/YYYY h:mm A") : "";
}

/**
 * @param {Record<string, unknown>} user
 * @returns {string[]}
 */
export function userAdminRecordToCsvCells(user) {
  const emailRaw = user?.email_id != null ? String(user.email_id).trim() : "";
  return USER_ADMIN_CSV_COLUMN_IDS.map((colId) => {
    switch (colId) {
      case "first_name":
        return user?.first_name != null ? String(user.first_name).trim() : "";
      case "last_name":
        return user?.last_name != null ? String(user.last_name).trim() : "";
      case "email":
        return emailRaw && !isPlaceholderEmail(emailRaw) ? emailRaw : "";
      case "phone":
        return user?.phone_number != null ? String(user.phone_number).trim() : "";
      case "role":
        return getUserAdminCsvRoles(user).join(",");
      case "login_status":
        return user?.login_status != null && String(user.login_status).trim() !== ""
          ? String(user.login_status).trim()
          : "No Login";
      case "last_login":
        return formatUserAdminTimestampForCsv(user?.last_login);
      case "email_verified":
        return user?.email_verified ? "Yes" : "No";
      case "created":
        return formatUserAdminTimestampForCsv(user?.created_date);
      default:
        return "";
    }
  });
}

/**
 * @param {Record<string, unknown>[]} rows
 * @returns {string}
 */
export function buildUserAdminCsvExportText(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const lines = [
    USER_ADMIN_CSV_HEADER_LINE,
    ...list.map((row) => userAdminRecordToCsvCells(row).map(escapeCsvCell).join(",")),
  ];
  return lines.join("\r\n");
}
